/**
 * Catalog index source for the BROWSER (swapped in for
 * `catalog-index-source.ts` by `clientOverrides` in vite.config.ts).
 *
 * English (the default locale, the majority of traffic) ships inside the entry
 * chunk, so English pages hydrate with zero extra round trips. German, French
 * and Italian are separate chunks that `prepareClientRuntime()` loads BEFORE
 * hydration (src/client.tsx), so `getCatalogIndexSource` is still synchronous
 * wherever React needs it. A visitor never downloads the other locales.
 */
import type { Locale } from '../lib/i18n/config'
import type { CatalogIndex } from './catalog-index-projection'
import en from './generated/catalog-index.en.json'

const loaded: Partial<Record<Locale, CatalogIndex>> = {
  en: en as CatalogIndex,
}

const loaders: Record<Exclude<Locale, 'en'>, () => Promise<CatalogIndex>> = {
  de: () =>
    import('./generated/catalog-index.de.json').then(
      (m) => m.default as CatalogIndex,
    ),
  fr: () =>
    import('./generated/catalog-index.fr.json').then(
      (m) => m.default as CatalogIndex,
    ),
  it: () =>
    import('./generated/catalog-index.it.json').then(
      (m) => m.default as CatalogIndex,
    ),
}

export function getCatalogIndexSource(locale: Locale): CatalogIndex {
  const index = loaded[locale]
  if (index) return index
  // Only reachable if hydration started without `prepareClientRuntime()`.
  // Falling back keeps the UI alive (English nav) instead of crashing.
  if (import.meta.env.DEV) {
    console.warn(`[catalog-index] locale "${locale}" used before it was loaded`)
  }
  return loaded.en as CatalogIndex
}

export async function loadCatalogIndex(locale: Locale): Promise<void> {
  if (loaded[locale] || locale === 'en') return
  loaded[locale] = await loaders[locale]()
}
